import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { fetchUserData, logout } from "../redux/authSlice";
import { useNavigate } from "react-router-dom";
import logoImg from "../assets/ebuos/logo.png";
import {
  Bell, Camera, Shield, ShieldCheck, Pencil,
  Settings, LogOut, Copy, Users, ChevronRight,
  LayoutDashboard, ScanLine, CreditCard, Mail,
  BookOpen, Store, BarChart2, Phone, Info, Lock,
  Check,
} from "lucide-react";

/* ─── Brand ─────────────────────────────────────────────── */
const G = {
  blue:   "#4285F4",
  red:    "#EA4335",
  green:  "#34A853",
  yellow: "#FBBC05",
  white:  "#FFFFFF",
  bg:     "#F8F9FA",
  text:   "#202124",
  soft:   "#5F6368",
  border: "#E8EAED",
  card:   "#FFFFFF",
  dark:   "#1a1a2e",
};

const GRAD  = "linear-gradient(135deg, #4285F4 0%, #EA4335 35%, #FBBC05 65%, #34A853 100%)";
const GRAD2 = "linear-gradient(90deg, #4285F4, #EA4335, #FBBC05, #34A853)";

const FONT = `
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@300;400;600;700;800;900&family=DM+Sans:wght@300;400;500;600&display=swap');
@keyframes gradSpin {
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}
.grad-ring { position: relative; border-radius: 50%; display: inline-flex; }
.grad-ring::before {
  content: '';
  position: absolute; inset: -3px; border-radius: 50%;
  background: linear-gradient(90deg, #4285F4, #EA4335, #FBBC05, #34A853, #4285F4);
  background-size: 300% 100%;
  animation: gradSpin 3s linear infinite;
  z-index: 0;
}
.grad-ring > * { position: relative; z-index: 1; }
.profile-fade { animation: fadeUp 0.4s ease both; }
.desk-only  { display: none !important; }
.mob-only   { display: block !important; }
@media (min-width: 900px) {
  .desk-only { display: block !important; }
  .mob-only  { display: none !important; }
}
`;

/* ─── Helpers ────────────────────────────────────────────── */
const maskName  = (n: string) => !n ? "" : n.length <= 2 ? n : n.slice(0,2) + "·".repeat(n.length-2);
const maskEmail = (e: string) => { if (!e) return ""; const [u,d] = e.split("@"); return d ? `${u.slice(0,3)}···@${d}` : e; };

/* ─── TOOLS config ───────────────────────────────────────── */
const TOOLS = [
  { icon: ScanLine,   label: "Scan & Go",   sub: "Self-checkout tool",        color: G.blue,   route: "scanner"    },
  { icon: CreditCard, label: "Payments",    sub: "M-Pesa, cards & more",      color: G.green,  route: "payments"   },
  { icon: Mail,       label: "Mailing",     sub: "Email marketing",           color: G.red,    route: "mailing"    },
  { icon: BookOpen,   label: "QuickBooks",  sub: "Accounting & invoicing",    color: G.yellow, route: "quickbooks" },
];

/* ══════════════════════════════════════════════════════════
   REUSABLE PIECES
══════════════════════════════════════════════════════════ */

/** Logo circle with animated gradient ring */
const LogoRing: React.FC<{ size?: number }> = ({ size = 52 }) => (
  <div className="grad-ring">
    <div style={{ width: size, height: size, borderRadius: "50%", background: G.white, padding: 5, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
      <img src={logoImg} alt="eBUOS" style={{ width: "100%", height: "100%", objectFit: "contain" }}
        onError={e => { (e.target as HTMLImageElement).style.display = "none"; }}
      />
    </div>
  </div>
);

/** Top gradient bar */
const GradBar = () => (
  <div style={{ height: 4, background: GRAD2, backgroundSize: "300% 100%", animation: "gradSpin 3s linear infinite" }} />
);

/** Section heading */
const SectionHead: React.FC<{ label: string }> = ({ label }) => (
  <div style={{ fontSize: 10, fontWeight: 800, color: G.soft, letterSpacing: "0.15em", marginBottom: 12, fontFamily: "'Nunito', sans-serif" }}>
    {label}
  </div>
);

/** List row */
const ListRow: React.FC<{ icon: React.ReactNode; label: string; sub?: string; onClick: () => void; color?: string; badge?: string }> = ({ icon, label, sub, onClick, color = G.blue, badge }) => (
  <button
    onClick={onClick}
    style={{
      width: "100%", background: G.white, border: `1px solid ${G.border}`,
      borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "center",
      gap: 14, cursor: "pointer", marginBottom: 8, textAlign: "left",
      transition: "border-color 0.2s, box-shadow 0.2s", fontFamily: "'Nunito', sans-serif",
    }}
    onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.boxShadow = `0 4px 16px ${color}18`; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = G.border; e.currentTarget.style.boxShadow = "none"; }}
  >
    <div style={{ width: 38, height: 38, borderRadius: 10, background: color + "15", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {icon}
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: G.text }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: G.soft, marginTop: 1 }}>{sub}</div>}
    </div>
    {badge
      ? <span style={{ background: color, color: G.white, fontSize: 10, fontWeight: 800, borderRadius: 20, padding: "3px 10px" }}>{badge}</span>
      : <ChevronRight size={16} color={G.soft} />
    }
  </button>
);

/** Tool card */
const ToolCard: React.FC<{ icon: React.ReactNode; label: string; sub: string; color: string; onClick: () => void }> = ({ icon, label, sub, color, onClick }) => (
  <button
    onClick={onClick}
    style={{
      flex: 1, minWidth: 120, background: G.white, border: `1px solid ${G.border}`,
      borderRadius: 14, padding: "18px 14px", display: "flex", flexDirection: "column",
      gap: 10, cursor: "pointer", textAlign: "left",
      transition: "transform 0.2s, box-shadow 0.2s, border-color 0.2s",
      fontFamily: "'Nunito', sans-serif",
    }}
    onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = `0 8px 24px ${color}22`; e.currentTarget.style.borderColor = color; }}
    onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = G.border; }}
  >
    <div style={{ width: 40, height: 40, borderRadius: 10, background: color + "15", display: "flex", alignItems: "center", justifyContent: "center" }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 12, fontWeight: 800, color: G.text }}>{label}</div>
      <div style={{ fontSize: 10, color: G.soft, marginTop: 2 }}>{sub}</div>
    </div>
  </button>
);

/* ══════════════════════════════════════════════════════════
   REFERRAL BLOCK
══════════════════════════════════════════════════════════ */
const ReferralBlock: React.FC<{ username?: string }> = ({ username }) => {
  const [copied, setCopied] = useState(false);
  if (!username) return null;
  const link = `https://ebuos.com/ref/${username}`;
  const copy = async () => { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const share = async () => { if (navigator.share) await navigator.share({ title: "Join eBUOS", url: link }); };

  return (
    <div style={{ background: G.dark, borderRadius: 16, padding: 20, marginTop: 8 }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: "rgba(255,255,255,0.5)", letterSpacing: "0.15em", marginBottom: 8, fontFamily: "'Nunito', sans-serif" }}>REFER & EARN</div>
      <div style={{ fontSize: 16, fontWeight: 800, color: G.white, marginBottom: 6, fontFamily: "'Nunito', sans-serif" }}>Invite businesses, earn $10 each</div>
      <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, marginBottom: 14, lineHeight: 1.6, fontFamily: "'DM Sans', sans-serif" }}>
        Share your link — you both earn $10 when they subscribe.
      </p>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <input readOnly value={link} style={{ flex: 1, padding: "10px 12px", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, fontSize: 11, color: "rgba(255,255,255,0.6)", background: "rgba(255,255,255,0.05)", fontFamily: "monospace", outline: "none" }} />
        <button onClick={copy} style={{ background: copied ? G.green : G.white, color: copied ? G.white : G.text, border: "none", borderRadius: 10, padding: "10px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 800, fontFamily: "'Nunito', sans-serif", transition: "background 0.2s" }}>
          {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied!" : "Copy"}
        </button>
      </div>
      <button onClick={share} style={{ width: "100%", background: "rgba(255,255,255,0.07)", color: G.white, border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, padding: "11px 0", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 12, fontWeight: 700, fontFamily: "'Nunito', sans-serif" }}>
        <Users size={14} /> Invite Businesses
      </button>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   PROFILE PAGE
══════════════════════════════════════════════════════════ */
const Profile: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user } = useSelector((s: RootState) => s.auth);
  const userId   = user?.userId;

  useEffect(() => { dispatch(fetchUserData()); }, [dispatch]);

  const isVerified = user?.isVerified === true;
  const firstName  = maskName(user?.firstName || "First");
  const lastName   = maskName(user?.lastName  || "Last");
  const email      = maskEmail(user?.emailAddress || user?.email || "");
  const photo      = user?.profilePicture || null;
  const initials   = `${user?.firstName?.[0]||""}${user?.lastName?.[0]||""}`.toUpperCase() || "U";

  const goTool = (route: string) => navigate(userId ? `/tools/${route}/${userId}` : "/AuthSwitch");

  /* Avatar with gradient ring */
  const Avatar = ({ size = 76 }: { size?: number }) => (
    <div className="grad-ring" style={{ flexShrink: 0 }}>
      <div style={{ width: size, height: size, borderRadius: "50%", background: G.bg, padding: 3, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {photo
          ? <img src={photo} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
          : <div style={{ width: "100%", height: "100%", borderRadius: "50%", background: GRAD, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ color: G.white, fontSize: size * 0.32, fontWeight: 900, fontFamily: "'Nunito', sans-serif" }}>{initials}</span>
            </div>
        }
      </div>
    </div>
  );

  /* ── Logged out ── */
  if (!user) return (
    <div style={{ minHeight: "100vh", background: G.bg, fontFamily: "'Nunito', sans-serif" }}>
      <style>{FONT}</style>
      <GradBar />
      <div style={{ maxWidth: 480, margin: "0 auto", padding: "60px 24px", textAlign: "center" }}>
        <LogoRing size={64} />
        <h2 style={{ fontSize: 26, fontWeight: 900, color: G.text, margin: "20px 0 8px", fontFamily: "'Nunito', sans-serif" }}>Your eBUOS Profile</h2>
        <p style={{ color: G.soft, marginBottom: 28, fontSize: 14 }}>Sign in to manage your business tools</p>
        <button onClick={() => navigate("/AuthSwitch")} style={{ padding: "13px 40px", background: GRAD, color: G.white, border: "none", borderRadius: 12, fontWeight: 800, fontSize: 15, cursor: "pointer", fontFamily: "'Nunito', sans-serif" }}>
          Sign In
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: G.bg, fontFamily: "'Nunito', sans-serif" }}>
      <style>{FONT}</style>

      {/* ══ NAVBAR ══ */}
      <nav style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(255,255,255,0.95)", backdropFilter: "blur(16px)", borderBottom: `1px solid ${G.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 24px", height: 62 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => navigate("/")}>
          <LogoRing size={36} />
          <span style={{ fontSize: 18, fontWeight: 900, background: GRAD2, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", fontFamily: "'Nunito', sans-serif" }}>eBUOS</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={() => navigate(`/notifications/${userId}`)} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 10, width: 38, height: 38, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <Bell size={18} color={G.soft} />
          </button>
          <button onClick={() => navigate(`/profilesettings/${userId}`)} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 10, width: 38, height: 38, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <Settings size={18} color={G.soft} />
          </button>
        </div>
      </nav>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "28px 20px 60px" }} className="profile-fade">

        {/* ══ PROFILE CARD ══ */}
        <div style={{ background: G.white, borderRadius: 20, border: `1px solid ${G.border}`, overflow: "hidden", marginBottom: 20, boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
          <GradBar />
          <div style={{ padding: "28px 24px", display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
            <div style={{ position: "relative" }}>
              <Avatar size={80} />
              <button onClick={() => navigate(`/editprofile/${userId}`)} style={{ position: "absolute", bottom: 0, right: 0, width: 26, height: 26, borderRadius: "50%", background: G.blue, border: `2px solid ${G.white}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                <Camera size={11} color={G.white} />
              </button>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: G.text, fontFamily: "'Nunito', sans-serif", letterSpacing: "-0.3px" }}>
                {firstName} {lastName}
              </div>
              <div style={{ fontSize: 13, color: G.soft, marginTop: 2 }}>{email}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 12px", borderRadius: 20, background: isVerified ? G.green + "15" : G.border, border: `1px solid ${isVerified ? G.green : G.border}` }}>
                  {isVerified ? <ShieldCheck size={11} color={G.green} /> : <Shield size={11} color={G.soft} />}
                  <span style={{ fontSize: 10, fontWeight: 800, color: isVerified ? G.green : G.soft, letterSpacing: "0.1em" }}>
                    {isVerified ? "VERIFIED" : "UNVERIFIED"}
                  </span>
                </div>
                {!isVerified && (
                  <button onClick={() => navigate(`/VerifyIntro/${userId}`)} style={{ fontSize: 11, fontWeight: 800, color: G.blue, background: G.blue + "12", border: "none", borderRadius: 20, padding: "4px 12px", cursor: "pointer" }}>
                    Verify Now →
                  </button>
                )}
              </div>
            </div>
            <button onClick={() => navigate(`/editprofile/${userId}`)} style={{ padding: "10px 20px", background: G.bg, border: `1px solid ${G.border}`, borderRadius: 10, fontWeight: 700, fontSize: 13, color: G.text, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontFamily: "'Nunito', sans-serif" }}>
              <Pencil size={14} /> Edit Profile
            </button>
          </div>
        </div>

        {/* ══ TOOLS ══ */}
        <div style={{ marginBottom: 20 }}>
          <SectionHead label="YOUR TOOLS" />
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {TOOLS.map(t => (
              <ToolCard key={t.route} icon={<t.icon size={20} color={t.color} />} label={t.label} sub={t.sub} color={t.color} onClick={() => goTool(t.route)} />
            ))}
          </div>
        </div>

        {/* ══ BUSINESS ══ */}
        <div style={{ marginBottom: 20 }}>
          <SectionHead label="BUSINESS" />
          <ListRow icon={<Store size={18} color={G.blue} />}      label="eBUOS for Business"   sub="Onboard or manage your store"     onClick={() => navigate(userId ? `/commerce-onboarding/${userId}` : "/AuthSwitch")} color={G.blue} badge="List Store" />
          <ListRow icon={<LayoutDashboard size={18} color={G.green} />} label="Business Dashboard" sub="View analytics & performance"   onClick={() => navigate("/dashboard")} color={G.green} />
          <ListRow icon={<BarChart2 size={18} color={G.yellow} />} label="Analytics"             sub="Revenue, sales & reports"         onClick={() => navigate("/analytics")} color={G.yellow} />
        </div>

        {/* ══ ACCOUNT ══ */}
        <div style={{ marginBottom: 20 }}>
          <SectionHead label="ACCOUNT" />
          <ListRow icon={<Pencil size={18} color={G.blue} />}        label="Edit Profile"        sub="Update your personal info"       onClick={() => navigate(`/editprofile/${userId}`)}     color={G.blue} />
          <ListRow icon={<ShieldCheck size={18} color={G.green} />}  label="Verify Account"      sub="Complete identity verification"  onClick={() => navigate(`/VerifyIntro/${userId}`)}    color={G.green} />
          <ListRow icon={<Settings size={18} color={G.soft} />}      label="Settings & Privacy"  sub="Security & preferences"          onClick={() => navigate(`/profilesettings/${userId}`)} color={G.soft} />
          <ListRow icon={<Lock size={18} color={G.soft} />}          label="Login & Security"    sub="Password & 2FA"                  onClick={() => navigate(`/loginsecurity/${userId}`)}   color={G.soft} />
          <ListRow icon={<Bell size={18} color={G.soft} />}          label="Notifications"       sub="Alerts & updates"                onClick={() => navigate(`/notifications/${userId}`)}   color={G.soft} />
        </div>

        {/* ══ SUPPORT ══ */}
        <div style={{ marginBottom: 20 }}>
          <SectionHead label="SUPPORT" />
          <ListRow icon={<Phone size={18} color={G.blue} />}  label="Contact Us"   sub="Get help from our team"    onClick={() => navigate("/contact")} color={G.blue} />
          <ListRow icon={<Info size={18} color={G.soft} />}   label="About eBUOS"  sub="Our story & mission"       onClick={() => navigate("/about")}   color={G.soft} />
          <ListRow icon={<Lock size={18} color={G.soft} />}   label="Privacy Policy" sub="How we protect your data" onClick={() => navigate("/privacy")} color={G.soft} />
        </div>

        {/* ══ REFERRAL ══ */}
        <div style={{ marginBottom: 20 }}>
          <SectionHead label="REFERRALS" />
          <ReferralBlock username={user?.username} />
        </div>

        {/* ══ LOGOUT ══ */}
        <button
          onClick={async () => { await dispatch(logout()).unwrap(); navigate("/AuthSwitch"); }}
          style={{
            width: "100%", padding: "14px", background: G.white,
            border: `2px solid ${G.red}`, borderRadius: 12,
            color: G.red, fontWeight: 800, fontSize: 14,
            cursor: "pointer", display: "flex", alignItems: "center",
            justifyContent: "center", gap: 8,
            fontFamily: "'Nunito', sans-serif",
            transition: "background 0.2s, color 0.2s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = G.red; e.currentTarget.style.color = G.white; }}
          onMouseLeave={e => { e.currentTarget.style.background = G.white; e.currentTarget.style.color = G.red; }}
        >
          <LogOut size={17} /> Sign Out
        </button>

        {/* Copyright */}
        <p style={{ textAlign: "center", fontSize: 12, color: G.soft, marginTop: 28, fontFamily: "'DM Sans', sans-serif" }}>
          © 2026{new Date().getFullYear() > 2026 ? `–${new Date().getFullYear()}` : ""} eBUOS. The Business Operating System.
        </p>
      </div>
    </div>
  );
};

export default Profile;